import React, { useState, useMemo } from 'react'
import Table from 'components/Table';
import { Checkbox, Button, Icon } from 'semantic-ui-react';
import { numFormat } from '@rededompedro/utils';
import { DateTime } from 'luxon';
import produce from 'immer';
import request from 'services/http';
import { toast } from 'react-toastify';
import { tap, finalize } from 'rxjs/operators';
import { useSubscription } from 'utils';


export default function Detalhamento({ setDetalhamento, detalhamento, tipo, operacao, periodo, registros, setRegistros, emp, setEmp, consolidado }) {

    const [registrosSelecionados, setRegistrosSelecionados] = useState([])
    const [isSubmiting, setIsSubmiting] = useState(false)

    function handleSubmit() {
        if (registrosSelecionados.length) 
            setIsSubmiting(true)
        else 
            toast.info('Selecione pelo menos um registro!')
    }

    function handleCheckboxChange(_, { value, checked }) {
        if (checked && !registrosSelecionados.includes(value)) {
            setRegistrosSelecionados(produce(registrosSelecionados, draft => {
                draft.push(value)
            }))
        } else if (!checked && registrosSelecionados.includes(value)) {
            setRegistrosSelecionados(produce(registrosSelecionados, draft => {
                let idx = draft.findIndex(x => x === value)
                draft.splice(idx, 1)
            }))
        }
    }

    function handleMarkAllChange(_, { checked }) {
        if (checked)
            setRegistrosSelecionados(detalhamento.filter(x => !x.alreadyUsed).map(x => tipo === 0 ? x.seq : x.id) )
        else
            setRegistrosSelecionados([])
    }

    const btnSubmit = (
        <Button animated='fade' className='w-25' color='green' onClick={handleSubmit} loading={isSubmiting} disabled={isSubmiting}>
            <Button.Content visible>Remover Selecionados</Button.Content>
            <Button.Content hidden><Icon name='send' /></Button.Content>
        </Button>
    )

    const atualizaPDD$ = useMemo(() => request({ 
        url: '/dre/pdd', 
        method: 'PATCH', 
        body: { 
            empresa: emp._id, 
            tipo, operacao, 
            periodo, registrosSelecionados, 
            deletedAt: DateTime.local().toFormat('dd/MM/yyyy HH:mm:ss') 
        } 
    }).pipe( 
        tap(() => {
            toast.success('PDD atualizado!')
            const dets = produce(detalhamento, draft => {
                const indices = []
                for (const id of registrosSelecionados) {
                    let idx = draft.findIndex(x => x && ((tipo === 0 && x.seq === id) || (tipo !== 0 && x.id === id)))
                    if (idx >= 0)
                        indices.push(idx)
                }
                return draft.filter((_, i) => !indices.includes(i))
            })
            const regs = produce(registros, draft => {
                const idx = draft.findIndex(x => x && ((tipo !== 0 && x.empresa === emp._id) || (tipo === 0 && x.empresa === emp.codDpnet)))
                const vl = dets.map(x => x.valor).reduce((a, b) => a + b, 0)
                draft[idx].valor = vl
                if (vl === 0)
                    draft.splice(idx, 1)
            })
            setRegistros(regs)
            setRegistrosSelecionados([])
            setDetalhamento(dets.length === 0 ? null : dets)
            if (dets.length === 0)
                setEmp(null)
        }),
        finalize(() => setIsSubmiting(false))
    ), [emp._id, emp.codDpnet, periodo, registros, operacao, registrosSelecionados, detalhamento, setRegistros, setDetalhamento, tipo, setEmp])

    useSubscription(atualizaPDD$, isSubmiting)

    const config = tipo !== 0 ? {
        columns: !consolidado ? [
            { field: 'codigo', text: 'Código', sort: true },
            { field: 'cliente', text: 'Cliente', sort: true },
            { field: 'documento', text: 'Fatura', sort: true },
            { field: 'emissao', text: 'Emissão', sort: true },
            { field: 'vencto', text: 'Vencimento', sort: true },
            { field: 'valor', text: 'Valor', formatter: value => numFormat(value), sort: true },
            { field: 'selection', text: <Checkbox label='Marcar todos' onChange={handleMarkAllChange} />, sort: false, cellProps: { textAlign: 'center' } },
        ] : [
            { field: 'codigo', text: 'Código', sort: true },
            { field: 'cliente', text: 'Cliente', sort: true },
            { field: 'documento', text: 'Fatura', sort: true },
            { field: 'emissao', text: 'Emissão', sort: true },
            { field: 'vencto', text: 'Vencimento', sort: true },
            { field: 'valor', text: 'Valor', formatter: value => numFormat(value), sort: true },
        ],
        footer: !consolidado ? [
            {
                columns: [
                    { colSpan: 5, content: 'Total Geral', textAlign: 'center', style: { bottom: 60, zIndex: 6 } },
                    { colSpan: 2, content: numFormat(detalhamento.map(x => x.valor).reduce((a, b) => a + b, 0)), style: { bottom: 60 } }
                ]
            },
            {
                className: 'd-print-none',
                columns: [
                    { colSpan: 5, content: 'Total Marcado', textAlign: 'center', style: { bottom: 30, zIndex: 6 } },
                    { colSpan: 2, content: numFormat((detalhamento.filter(x => registrosSelecionados.includes(x.id)).map(x => x.valor).reduce((a, b) => a + b, 0))), style: { bottom: 30, zIndex: 6 } }
                ]
            },
            {
                className: 'd-print-none',
                columns: [
                    { colSpan: 5, content: 'Total Disponível', textAlign: 'center', style: { bottom: 0, zIndex: 6 } },
                    { colSpan: 2, content: numFormat((detalhamento.filter(x => !x.alreadyUsed).map(x => x.valor).reduce((a, b) => a + b, 0) - detalhamento.filter(x => registrosSelecionados.includes(x.id)).map(x => x.valor).reduce((a, b) => a + b, 0))), style: { bottom: 0, zIndex: 6 } }
                ]
            },
            {
                className: 'd-print-none',
                columns: [
                    { className: 'actions', colSpan: 7, content: btnSubmit, textAlign: 'right' }
                ]
            }
        ] : [
            {
                columns: [
                    { colSpan: 5, content: 'Total', textAlign: 'center', style: { bottom: 0, zIndex: 6 } },
                    { colSpan: 1, content: numFormat(detalhamento.map(x => x.valor).reduce((a, b) => a + b, 0)), style: { bottom: 0 } }
                ]
            }
        ],
        data: detalhamento.length ? detalhamento.map(x => {
            return {
                "codigo": x.codigo,
                "cliente": x.cliente,
                "documento": x.documento,
                "emissao": DateTime.fromFormat(x.emissao + "", "yyyyMMddHHmmss").toFormat('dd/MM/yyyy HH:mm:ss'),
                "vencto": DateTime.fromFormat(x.vencto + "", "yyyyMMdd").toFormat('dd/MM/yyyy'),
                "valor": x.valor,
                "selection": !consolidado ? !x.alreadyUsed ? <Checkbox value={x.id} checked={registrosSelecionados.includes(x.id)} onChange={handleCheckboxChange} /> : '' : '',
                "rowProps": { disabled: x.alreadyUsed }
            }
        }) : [],
        filter: true
    } : {
        columns: !consolidado ? [
            { field: 'cliente', text: 'Cliente', sort: true },
            { field: 'data', text: 'Data', sort: true },
            { field: 'motivo', text: 'Motivo', sort: true },
            { field: 'cheque', text: 'Nº Cheque', sort: true },
            { field: 'valor', text: 'Valor', formatter: value => numFormat(value), sort: true },
            { field: 'selection', text: <Checkbox label='Marcar todos' onChange={handleMarkAllChange} />, sort: false, cellProps: { textAlign: 'center' } },
        ] : [
            { field: 'cliente', text: 'Cliente', sort: true },
            { field: 'data', text: 'Data', sort: true },
            { field: 'motivo', text: 'Motivo', sort: true },
            { field: 'cheque', text: 'Nº Cheque', sort: true },
            { field: 'valor', text: 'Valor', formatter: value => numFormat(value), sort: true }
        ],
        footer: !consolidado ? [
            {
                columns: [
                    { colSpan: 4, content: 'Total Geral', textAlign: 'center', style: { bottom: 60, zIndex: 6 } },
                    { colSpan: 2, content: numFormat(detalhamento.map(x => x.valor).reduce((a, b) => a + b, 0)), style: { bottom: 60 } }
                ]
            },
            {
                className: 'd-print-none',
                columns: [
                    { colSpan: 4, content: 'Total Marcado', textAlign: 'center', style: { bottom: 30, zIndex: 6 } },
                    { colSpan: 2, content: numFormat((detalhamento.filter(x => registrosSelecionados.includes(x.seq)).map(x => x.valor).reduce((a, b) => a + b, 0))), style: { bottom: 30, zIndex: 6 } }
                ]
            },
            {
                className: 'd-print-none',
                columns: [
                    { colSpan: 4, content: 'Total Disponível', textAlign: 'center', style: { bottom: 0, zIndex: 6 } },
                    { colSpan: 2, content: numFormat((detalhamento.filter(x => !x.alreadyUsed).map(x => x.valor).reduce((a, b) => a + b, 0) - detalhamento.filter(x => registrosSelecionados.includes(x.seq)).map(x => x.valor).reduce((a, b) => a + b, 0))), style: { bottom: 0, zIndex: 6 } }
                ]
            },
            {
                className: 'd-print-none',
                columns: [
                    { className: 'actions', colSpan: 6, content: btnSubmit, textAlign: 'right' }
                ]
            }
        ] : [
            {
                columns: [
                    { colSpan: 4, content: 'Total', textAlign: 'center', style: { bottom: 0, zIndex: 6 } },
                    { colSpan: 1, content: numFormat(detalhamento.map(x => x.valor).reduce((a, b) => a + b, 0)), style: { bottom: 0 } }
                ]
            }
        ],
        data: detalhamento.length ? detalhamento.map(x => {
            return {
                "cliente": x.cliente,
                "data": x.data_emissao,
                "motivo": x.motivo,
                "cheque": x.numero_cheque,
                "valor": x.valor,
                "selection": !x.alreadyUsed ? <Checkbox value={x.seq} checked={registrosSelecionados.includes(x.seq)} onChange={handleCheckboxChange} /> : '',
                "rowProps": { disabled: x.alreadyUsed }
            }
        }) : [],
        filter: true
    }

    return (
        <Table id='table-relatorios-exibicao-det' compact sortable selectable {...config} />
    )
}
